import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTolgee, useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudSelect,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { useExpiryCorrectionGate } from '../../../features/corrections/expiry-correction-gate';
import {
  exportSubmissionCsv,
  getSubmission,
  type SubmissionDetail,
  type SubmissionDetailRow,
} from '../../../features/reports/api';
import { isNormalizedApiError } from '../../../app/api-client';
import { useSession } from '../../../hooks/use-session';
import { CorrectionExpiryModal } from './CorrectionExpiryModal';
import { downloadCsv } from './csv';
import {
  Breadcrumb,
  EMPTY_CELL,
  FilterBar,
  formatDate,
  formatDateTime,
  formatExpiry,
  PageHeader,
  useToast,
  type ActiveFilter,
} from '@simsm/ui-shared';
import { DEFAULT_LOCALE } from '@simsm/ui-i18n';
import {
  correctionDeadline,
  matchesMovementFilter,
  stockMovementReasonSchema,
  withinCorrectionWindow,
} from '@simsm/shared-types';

// caiet UC51 is the Registrar's use case: the roles that may ask for an expiry
// correction are the ones the wireframe lists (`RaportDetalii.tsx:18`), and the
// caiet's role table gives the System Administrator only "aprobă solicitări
// (UC22)". The button used to be rendered for every role and always failed —
// POST /corrections answered 404 "no registrar scope on session" and the modal
// showed the generic "Eroare la trimiterea cererii" (audit E4).
const MAY_REQUEST_CORRECTION = ['REGISTRAR_ADMIN', 'REGISTRAR_OPERATOR', 'REGISTRAR_OPERATOR_IMSP'];

// The reasons this application knows how to name. Anything else is printed as
// stored rather than as a broken translation key — a raw enum is bad, a raw key
// is worse.
const KNOWN_REASONS: ReadonlySet<string> = new Set(stockMovementReasonSchema.options);

// UC50-06: corrections are admitted for `windowDays` calendar days from the
// Chisinau date the report was accepted on, the rule core-service applies
// (CF-13; docs/shape-ops-correction-window.md D2), and `deadlineIso` is the last
// admitted date. core-service is the authority and refuses past it; this is the
// affordance the wireframe draws and the application never had — the deadline
// stated before the click rather than a generic failure after it (audit E4).
// A report whose window the server did not state gets no claim either way: the
// control stays offered and the server still decides.
export function correctionWindow(
  confirmedAtIso: string,
  windowDays: number,
  now: number = Date.now(),
): { deadlineIso: string; open: boolean } {
  const confirmed = new Date(confirmedAtIso);
  if (windowDays <= 0 || Number.isNaN(confirmed.getTime())) {
    return { deadlineIso: '', open: true };
  }
  return {
    deadlineIso: correctionDeadline(confirmed, windowDays),
    open: withinCorrectionWindow(confirmed, windowDays, new Date(now)),
  };
}

/**
 * A detail row as the bff now sends it (spec 054 T073, research R9): the stock
 * row's id, the location it was filed at ('' for none) and the confirmation.
 */
type MovementRow = SubmissionDetailRow & { id?: string; siteId?: string; confirmedAt?: string };

/**
 * Where "Corectează mișcarea" leads for one row (spec 054 T076, FR-031): "Raport
 * nou" with the dialog in correction mode on that movement. Null for a row that
 * names no movement or no location, which that flow cannot correct.
 */
export function movementCorrectionPath(role: string, row: MovementRow): string | null {
  if (!row.id || !row.siteId) return null;
  return `/${role}/reporting/new?corectie=${encodeURIComponent(row.id)}`;
}

export function SubmissionDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const tolgee = useTolgee(['language']);
  const toast = useToast();
  const navigate = useNavigate();
  // role drives the trail back to the register: this page is reached under
  // /operator and /system-admin, so the crumb must stay in the active section.
  const { id = '', role = 'operator' } = useParams<{ id: string; role: string }>();
  const reportsPath = `/${role}/reports`;
  const session = useSession();
  const [correctionTarget, setCorrectionTarget] = useState<{
    medicineCode: string;
    medicineName: string;
    lot: string;
    currentExpiry: string;
    currentExpiryPrecision?: string | undefined;
  } | null>(null);
  // The wireframe filters the movements of one report by medicine or lot and by
  // reason (RaportDetalii.tsx:62-66). The whole report is already on the client,
  // so both criteria are local: no round trip, and no criterion in the address
  // for a page the register already links to by id. The export sends the same
  // two criteria and the bff applies the same predicate (UC93-11).
  const [rowQuery, setRowQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  const query = useQuery({
    queryKey: ['operator-submission', id],
    queryFn: () => getSubmission(id),
    enabled: Boolean(id),
  });

  const mayCorrect = MAY_REQUEST_CORRECTION.includes(session.data?.activeRole ?? '');
  const windowDays = query.data?.correctionWindowDays ?? 0;
  const { deadlineIso: correctionDeadlineIso, open: inCorrectionWindow } = correctionWindow(
    query.data?.confirmedAtIso ?? '',
    windowDays,
  );

  function reasonLabel(reason: string): string {
    return KNOWN_REASONS.has(reason) ? t(`stock_reporting.reason.${reason}`) : reason;
  }

  const rowColumns = [
    { key: 'medicineCode', label: t('submission_detail.column.medicine') },
    { key: 'lot', label: t('submission_detail.column.lot') },
    { key: 'lotExpiry', label: t('submission_detail.column.expiry') },
    { key: 'quantitySigned', label: t('submission_detail.column.quantity'), align: 'end' as const },
    { key: 'reason', label: t('submission_detail.column.reason') },
    { key: 'comment', label: t('submission_detail.column.comment') },
    ...(mayCorrect
      ? [
          {
            key: 'actions',
            label: t('submission_detail.column.actions'),
            align: 'end' as const,
            // Two actions now: "Corectează mișcarea" beside "Corectează expirare".
            width: '360px',
          },
        ]
      : []),
  ];

  const allRows = useMemo<MovementRow[]>(() => query.data?.rows ?? [], [query.data]);

  // UC51-03: "Corectează expirare" only on the lots core would accept a request
  // for, inside the report's window (docs/shape-ops-expiry-correction-gate.md D4).
  const expiryAction = useExpiryCorrectionGate({
    submissionId: id,
    rows: allRows,
    enabled: mayCorrect,
    reportWindow: {
      open: inCorrectionWindow,
      days: windowDays,
      deadlineIso: correctionDeadlineIso,
    },
  });

  // The reasons this report actually carries, so the facet never offers a value
  // that would empty the table.
  const reasonOptions = useMemo(
    () =>
      [...new Set(allRows.map((r) => r.reason))]
        .map((reason) => ({ value: reason, label: reasonLabel(reason) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ro')),
    // reasonLabel closes over `t`, which changes with the interface language.
    [allRows, t],
  );

  const visibleRows = useMemo(
    () =>
      allRows.filter((r) => matchesMovementFilter(r, { search: rowQuery, reason: reasonFilter })),
    [allRows, rowQuery, reasonFilter],
  );

  // UC93-11 — the file is the bff's: it holds the rows these criteria leave on
  // screen, headed in the interface language, and the export is reported to
  // MLog (docs/shape-ops-report-export.md §3 D5).
  async function exportMovements(report: SubmissionDetail): Promise<void> {
    setExporting(true);
    try {
      const file = await exportSubmissionCsv(
        report.id,
        { search: rowQuery, reason: reasonFilter },
        tolgee.getLanguage() ?? DEFAULT_LOCALE,
      );
      downloadCsv(`submission-${report.reportNumber}.csv`, file);
    } catch {
      toast({
        variant: 'error',
        title: t('submission_detail.action.export'),
        text: t('submission_detail.export.error'),
        closeLabel: t('common.close'),
      });
    } finally {
      setExporting(false);
    }
  }

  const activeFilters: ActiveFilter[] = reasonFilter
    ? [
        {
          key: 'reason',
          label: t('submission_detail.column.reason'),
          value: reasonLabel(reasonFilter),
          onRemove: () => setReasonFilter(''),
        },
      ]
    : [];

  const tableRows = useMemo(
    () =>
      visibleRows.map((r, i) => ({
        id: `row-${i}`,
        medicineCode: r.medicineCode,
        lot: r.lot,
        lotExpiry: formatExpiry(r.lotExpiry, r.lotExpiryPrecision),
        quantitySigned: String(r.quantitySigned),
        reason: reasonLabel(r.reason),
        comment: r.comment,
      })),
    // reasonLabel closes over `t`, which changes with the interface language.
    [visibleRows, t],
  );

  // The trail names the report the way its reader does: by its number, the last
  // segment of the wireframe's own crumbs (RaportDetalii.tsx:41). Before the
  // report loads — and on the not-found branch, which renders this trail — the
  // only name available is the one the address bar carried, which is the number
  // when it was dictated and a uuid when the register linked here.
  const crumbLabel = query.data?.reportNumber ?? (id.length > 24 ? `${id.slice(0, 8)}…` : id);

  // The wireframe's `crumbs` trail (RaportDetalii.tsx:41) as AGE's own
  // breadcrumb; it replaces a back button that was a raw <a> around a button.
  const trail = (
    <Breadcrumb
      label={t('common.breadcrumb.aria_label')}
      crumbs={[{ label: t('reports_register.title'), to: reportsPath }, { label: crumbLabel }]}
    />
  );

  if (query.isLoading) {
    return <p className="simsm-submission__loading">{t('submission_detail.loading')}</p>;
  }

  if (query.isError || !query.data) {
    // The api-client rejects with a NormalizedApiError ({ status, code }), never
    // with the raw axios error, so the status is read off the error itself.
    const notFound = isNormalizedApiError(query.error) && query.error.status === 404;
    return (
      <section className="simsm-submission">
        {trail}
        <div className="simsm-submission__empty">
          <p>{notFound ? t('submission_detail.not_found') : t('submission_detail.load_error')}</p>
        </div>
      </section>
    );
  }

  const s = query.data;

  return (
    <section className="simsm-submission">
      {trail}

      <PageHeader
        title={t('submission_detail.title', { number: s.reportNumber })}
        actions={
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            loading={exporting}
            disabled={exporting}
            onClick={() => void exportMovements(s)}
          >
            <MudIcon slot="icon-start" name="download" size={20} />
            {t('submission_detail.action.export')}
          </MudButton>
        }
      />

      <dl className="simsm-submission__meta">
        <div>
          <dt>{t('submission_detail.meta.confirmed')}</dt>
          <dd>{formatDateTime(s.confirmedAtIso)}</dd>
        </div>
        <div>
          <dt>{t('submission_detail.meta.source')}</dt>
          <dd>
            <MudTag
              variant={s.source === 'MANUAL' ? 'info' : 'status'}
              label={
                s.source === 'MANUAL'
                  ? t('submission_detail.meta.source.manual')
                  : t('submission_detail.meta.source.automated')
              }
            />
          </dd>
        </div>
        <div>
          <dt>{t('submission_detail.meta.operator')}</dt>
          {/* The wireframe names the person here (`RaportDetalii.tsx:48`). A
              report confirmed before specs/030-actor-names carries no name and
              none can be resolved for it (caiet UC10), so the field falls back
              to the identifier it has always shown; an automated report has no
              person and says so. The identifier stays monospaced, a name does
              not. */}
          {s.operatorName ? (
            <dd>{s.operatorName}</dd>
          ) : s.operatorIdnp ? (
            <dd className="simsm-submission__mono">{s.operatorIdnp}</dd>
          ) : (
            <dd>{t('reports_register.reported_by.registrar_system')}</dd>
          )}
        </div>
        <div>
          <dt>{t('submission_detail.meta.registrar')}</dt>
          <dd>{s.registrarName || s.registrarId}</dd>
        </div>
        <div>
          <dt>{t('submission_detail.meta.site')}</dt>
          <dd>{s.siteName || EMPTY_CELL}</dd>
        </div>
        <div>
          <dt>{t('submission_detail.meta.rows')}</dt>
          <dd>{s.rowCount}</dd>
        </div>
      </dl>

      {/* The wireframe states the closed window to the roles it concerns, as an
          info box, rather than leaving a disabled control unexplained
          (`RaportDetalii.tsx:59-61`). */}
      {mayCorrect && !inCorrectionWindow ? (
        <MudInfoBox emphasis="strong" variant="info" className="simsm-submission__window-notice">
          {t('submission_detail.correction_window.closed', {
            days: windowDays,
            deadline: formatDate(correctionDeadlineIso),
          })}
        </MudInfoBox>
      ) : null}

      <FilterBar
        search={{
          value: rowQuery,
          onChange: setRowQuery,
          placeholder: t('submission_detail.search.placeholder'),
          ariaLabel: t('submission_detail.search.aria_label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => {
          setRowQuery('');
          setReasonFilter('');
        }}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('submission_detail.filter.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <label className="simsm-filter-bar__field">
            <span className="simsm-filter-bar__field-label">
              {t('submission_detail.column.reason')}
            </span>
            <MudSelect
              size="medium"
              value={reasonFilter}
              aria-label={t('submission_detail.column.reason')}
              placeholder={t('submission_detail.filter.reason_all')}
              onMudChange={(e: CustomEvent<{ value: string }>) => setReasonFilter(e.detail.value)}
            >
              <option value="">{t('submission_detail.filter.reason_all')}</option>
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </label>
        }
      />

      <div className="simsm-submission__table">
        <MudTable
          className="simsm-table"
          columns={rowColumns}
          rows={tableRows}
          hoverable={false}
          rowStyle="divided"
          aria-label={t('submission_detail.table.aria_label')}
        >
          {visibleRows.flatMap((r, idx) => [
            // The report is filed against a nomenclator code; the reader needs
            // the medicine. The code stays beneath it as the filed identity
            // (audit E5).
            <span
              key={`med-${idx}`}
              slot={`cell-medicineCode-${idx}`}
              className="simsm-submission__medicine"
            >
              <span className="simsm-submission__medicine-name">
                {r.medicineName || r.medicineCode}
              </span>
              {r.medicineName ? (
                <code className="simsm-submission__mono">{r.medicineCode}</code>
              ) : null}
            </span>,
            ...(mayCorrect
              ? [
                  <span key={`act-${idx}`} slot={`cell-actions-${idx}`}>
                    {/* UC50-05/06: the movement itself, gated like the expiry
                        request beside it, by role and by the window. */}
                    {movementCorrectionPath(role, r) ? (
                      <>
                        <MudButton
                          shape="circular"
                          variant="neutral"
                          size="sm"
                          disabled={!inCorrectionWindow}
                          title={
                            inCorrectionWindow
                              ? undefined
                              : t('submission_detail.correction_window.closed_hint', {
                                  days: windowDays,
                                  deadline: formatDate(correctionDeadlineIso),
                                })
                          }
                          onClick={() => {
                            const path = movementCorrectionPath(role, r);
                            if (path) navigate(path);
                          }}
                        >
                          {t('submission_detail.action.correct_movement')}
                        </MudButton>{' '}
                      </>
                    ) : null}
                    <MudButton
                      shape="circular"
                      variant="neutral"
                      size="sm"
                      disabled={expiryAction(r).disabled}
                      title={expiryAction(r).title}
                      onClick={() =>
                        setCorrectionTarget({
                          medicineCode: r.medicineCode,
                          medicineName: r.medicineName ?? '',
                          lot: r.lot,
                          currentExpiry: r.lotExpiry,
                          currentExpiryPrecision: r.lotExpiryPrecision,
                        })
                      }
                    >
                      {t('submission_detail.action.correct_expiry')}
                    </MudButton>
                  </span>,
                ]
              : []),
          ])}
        </MudTable>
      </div>

      {correctionTarget ? (
        <CorrectionExpiryModal
          open={true}
          medicineCode={correctionTarget.medicineCode}
          medicineName={correctionTarget.medicineName}
          lot={correctionTarget.lot}
          currentExpiry={correctionTarget.currentExpiry}
          currentExpiryPrecision={correctionTarget.currentExpiryPrecision}
          onClose={() => setCorrectionTarget(null)}
          onSubmitted={() => setCorrectionTarget(null)}
        />
      ) : null}
    </section>
  );
}
